// External dependencies
// Always the default export, called as `LogRocket.track(...)`. In a browser the
// SDK exports a class instance whose methods read `this`; a named import
// compiles to `(0, mod.track)(...)`, which drops `this`, so every call threw a
// TypeError that the try/catch below swallowed and no custom event ever reached
// LogRocket (only the Node no-op polyfill has free functions).
import LogRocket from 'logrocket';

// Internal dependencies - Events and types
import type { EventName } from './events';
import { EVENTS } from './events';
import type { EventProps, NavigationEventProps } from './types';

// Type for LogRocket's track method properties
type TrackEventProperties = Record<string, string | number | boolean | undefined>;

/**
 * Converts a value to a format that LogRocket can handle
 * @param value - The value to convert
 * @returns A value that LogRocket can handle, or undefined if the value should be skipped
 */
function convertToTrackableValue(value: unknown): string | number | boolean | undefined {
    if (value === null || value === undefined) {
        return undefined;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return value;
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return String(value);
}

/**
 * Keys whose values are never reported, to LogRocket or to the server log.
 * Follows the network sanitizer in `LogRocketInitializer`: a CSRF mismatch, for
 * one, reports both token values as context. Booleans and numbers pass, so
 * `hasToken: false` still reads as it should.
 */
const SECRET_KEY = /token|secret|password|passwd|authorization|cookie|credential|api[-_]?key/i;

function redactSecret(key: string, value: unknown): unknown {
    if (!SECRET_KEY.test(key)) return value;
    return typeof value === 'boolean' || typeof value === 'number' ? value : '[redacted]';
}

function redactSecrets(context: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(
        Object.entries(context).map(([key, value]) => [key, redactSecret(key, value)]),
    );
}

/**
 * A context value as it goes into the server log: scalars as they are, anything
 * larger by its type alone. Some callers pass a whole request as context, and a
 * request's headers carry the session cookie, which must never reach a log line.
 */
function summariseForLog(value: unknown): unknown {
    if (typeof value === 'string') return value.length > 500 ? `${value.slice(0, 500)}…` : value;
    if (value === null || typeof value !== 'object') return value;
    if (Array.isArray(value)) return `[Array(${value.length})]`;
    return `[${value.constructor?.name ?? 'Object'}]`;
}

/**
 * Print an error report on the server, where LogRocket never runs.
 *
 * API routes, server actions and the auth config all report through this
 * class, and for most of them it is the only record an error ever gets. Shaped
 * like the Edge variant in `./edge` so the two read the same in the logs.
 */
function reportServerError(error: unknown, context: Record<string, unknown>) {
    const err = error instanceof Error ? error : new Error(String(error));
    const { source, ...rest } = redactSecrets(context);
    const summary = Object.fromEntries(
        Object.entries(rest).map(([key, value]) => [key, summariseForLog(value)]),
    );
    console.error('[Server Error]', {
        error: { name: err.name, message: err.message, stack: err.stack },
        source: source ?? 'Unknown source',
        ...(Object.keys(summary).length > 0 ? { context: summary } : {}),
    });
}

/**
 * The most calls held while the LogRocket provider decides whether LogRocket
 * starts. It decides in its mount effect, so this only bounds the pathological
 * case; past it, the oldest calls are dropped first.
 */
export const MAX_PENDING_CALLS = 100;

type PendingCall =
    | { type: 'event'; args: [EventName, EventProps] }
    | { type: 'metric'; args: [EventName, number, Record<string, unknown>?] }
    | { type: 'error'; args: [Error, Record<string, unknown>?] };

/**
 * - `pending`: the provider has not decided yet, so calls are held.
 * - `active`: LogRocket started, so calls go straight to the SDK.
 * - `off`: LogRocket is not starting in this tab, so calls are dropped.
 */
type TrackingState = 'pending' | 'active' | 'off';

/**
 * Tracking class that handles all analytics and error tracking
 * using LogRocket as the underlying tracking platform.
 *
 * This class provides methods to track:
 * - Custom events with properties
 * - Errors with context
 * - Performance metrics
 * - API requests
 * - Navigation between pages
 * - Memory usage
 * - Long running tasks
 */
export class Tracking {
    private state: TrackingState = 'pending';
    private isDevelopment = process.env.NODE_ENV === 'development';
    private pendingEvents: PendingCall[] = [];

    /**
     * Start sending. The LogRocket provider calls this straight after
     * `LogRocket.init()`, which is all the readiness there is to wait for: the
     * SDK holds calls itself until its recorder script has loaded.
     *
     * This used to poll `window.LogRocket` until it appeared. That global only
     * exists when LogRocket is loaded from a script tag, and this app imports the
     * npm package, which never creates it — so the poll never succeeded, and
     * nothing sent through this class reached LogRocket from any deployed build.
     */
    public init() {
        if (this.state === 'active') return;
        this.state = 'active';

        if (this.isDevelopment) {
            console.info(
                'Tracking initialized in development mode - events will be logged to console',
            );
        }

        const held = this.pendingEvents;
        this.pendingEvents = [];
        held.forEach((call) => this.dispatch(call));
    }

    /**
     * LogRocket is not starting in this tab: it is disabled, no project is
     * configured, or init failed. Drop what is held and ignore everything from
     * now on, rather than holding it for the life of the tab.
     */
    public disable() {
        this.state = 'off';
        this.pendingEvents = [];
    }

    private route(call: PendingCall) {
        // The server never starts LogRocket, so nothing is held there. Errors
        // are printed; events and metrics are dropped.
        if (typeof window === 'undefined') {
            if (call.type === 'error') reportServerError(call.args[0], call.args[1] ?? {});
            return;
        }

        if (this.state === 'active') {
            this.dispatch(call);
        } else if (this.state === 'pending') {
            if (this.pendingEvents.length >= MAX_PENDING_CALLS) this.pendingEvents.shift();
            this.pendingEvents.push(call);
        }
    }

    private dispatch(call: PendingCall) {
        try {
            switch (call.type) {
                case 'event':
                    this._trackEvent(call.args[0], call.args[1]);
                    break;
                case 'metric':
                    this._trackMetric(call.args[0], call.args[1], call.args[2]);
                    break;
                case 'error':
                    this._trackError(call.args[0], call.args[1]);
                    break;
            }
        } catch (error) {
            // Tracking must never throw into the code that called it.
            console.error('Failed to track:', error);
        }
    }

    private _trackEvent(eventName: EventName, properties: EventProps) {
        if (typeof window === 'undefined') return;

        if (this.isDevelopment) {
            console.log('[Tracking Event]', {
                event: eventName,
                properties: {
                    ...properties,
                    timestamp: Date.now(),
                },
            });
            return;
        }

        try {
            const trackProps: TrackEventProperties = {
                ...Object.entries(properties).reduce((acc, [key, value]) => {
                    const trackableValue = convertToTrackableValue(redactSecret(key, value));
                    if (trackableValue !== undefined) {
                        acc[key] = trackableValue;
                    }
                    return acc;
                }, {} as TrackEventProperties),
                timestamp: Date.now(),
            };

            LogRocket.track(eventName as string, trackProps);
        } catch (error) {
            console.error(`Failed to track event ${eventName}:`, error);
        }
    }

    private _trackMetric(name: EventName, value: number, context: Record<string, unknown> = {}) {
        if (typeof window === 'undefined') return;

        if (this.isDevelopment) {
            console.log('[Tracking Metric]', {
                name,
                value,
                context: {
                    ...context,
                    timestamp: Date.now(),
                },
            });
            return;
        }

        try {
            const trackProps: TrackEventProperties = {
                ...Object.entries(context).reduce((acc, [key, value]) => {
                    const trackableValue = convertToTrackableValue(redactSecret(key, value));
                    if (trackableValue !== undefined) {
                        acc[key] = trackableValue;
                    }
                    return acc;
                }, {} as TrackEventProperties),
                value,
                timestamp: Date.now(),
            };

            LogRocket.track(name as string, trackProps);
        } catch (error) {
            console.error(`Failed to track metric ${name}:`, error);
        }
    }

    private _trackError(error: Error, context: Record<string, unknown> = {}) {
        if (typeof window === 'undefined') return;

        if (this.isDevelopment) {
            const errorInfo = {
                name: error.name,
                message: error.message,
                stack: error.stack,
                source: context.source || 'Unknown source',
                originalError: error,
                context: {
                    ...context,
                    timestamp: Date.now(),
                },
            };

            console.group('%c[Error Tracked]', 'color: #ff5555; font-weight: bold;');
            console.error('Error Details:', {
                name: errorInfo.name,
                message: errorInfo.message,
                source: errorInfo.source,
            });
            console.error('Stack Trace:');
            console.error(errorInfo.stack);
            if (Object.keys(context).length > 0) {
                console.error('Additional Context:', context);
            }
            console.groupEnd();
            return;
        }

        try {
            LogRocket.captureException(error, {
                extra: {
                    ...redactSecrets(context),
                    message: error.message,
                    stack: error.stack || 'No stack trace',
                },
            });
        } catch (err) {
            console.error('Failed to track error:', err);
        }
    }

    public event<T extends EventProps>(eventName: EventName, properties: T) {
        this.route({ type: 'event', args: [eventName, properties] });
    }

    public error(error: Error, context: Record<string, unknown> = {}) {
        this.route({ type: 'error', args: [error, context] });
    }

    /**
     * Track an error with source information
     * @param error - The error to track
     * @param source - The source of the error (e.g., component name, function name)
     * @param context - Additional context about the error
     */
    public errorWithSource(error: Error, source: string, context: Record<string, unknown> = {}) {
        this.error(error, { ...context, source });
    }

    public metric(name: EventName, value: number, context: Record<string, unknown> = {}) {
        this.route({ type: 'metric', args: [name, value, context] });
    }

    /**
     * Track an API request's performance metrics
     *
     * @param endpoint - The API URL that was called
     * @param duration - How long the request took in milliseconds
     * @param status - The HTTP status code returned
     * @param success - Whether the request succeeded or failed
     *
     * Example:
     * tracking.api('/api/users', 342, 200, true)
     */
    public api(endpoint: string, duration: number, status: number, success: boolean) {
        if (typeof window === 'undefined') return;
        this.metric(EVENTS.PERFORMANCE.API_LATENCY, duration, {
            name: 'api_request',
            context: {
                endpoint,
                status,
                success,
            },
        });
    }

    /**
     * Track when users navigate between pages
     *
     * @param from - The page/route they came from
     * @param to - The page/route they're going to
     *
     * Example:
     * tracking.navigation('/home', '/settings')
     */
    public navigation(from: string, to: string) {
        if (typeof window === 'undefined') return;
        this.event<NavigationEventProps>(EVENTS.NAVIGATION.PAGE_VIEW, {
            from,
            to,
            url: window.location.pathname,
            duration: performance.now(),
        });
    }

    /**
     * Track the application's memory usage metrics
     * Only works in browsers that support the memory API
     *
     * Example:
     * tracking.memory()
     */
    public memory() {
        if (typeof window === 'undefined') return;
        if ('memory' in performance) {
            // Type assertion for browsers that support memory API
            const memory = performance as Performance & {
                memory: {
                    usedJSHeapSize: number;
                    totalJSHeapSize: number;
                    jsHeapSizeLimit: number;
                };
            };

            if (memory.memory) {
                this.metric(EVENTS.PERFORMANCE.MEMORY, memory.memory.usedJSHeapSize, {
                    name: 'memory_usage',
                    context: {
                        totalHeap: memory.memory.totalJSHeapSize,
                        heapLimit: memory.memory.jsHeapSizeLimit,
                    },
                });
            }
        }
    }

    /**
     * Start tracking long-running tasks that might affect performance
     * Uses the PerformanceObserver API to monitor tasks that take too long
     *
     * @returns A cleanup function to stop tracking when no longer needed
     *
     * Example:
     * const cleanup = tracking.initLongTaskTracking();
     * // Later: cleanup();
     */
    public initLongTaskTracking(): () => void {
        if (typeof window === 'undefined') return () => {};
        if ('PerformanceObserver' in window) {
            // Create observer to monitor long tasks
            const observer = new PerformanceObserver((list) => {
                list.getEntries().forEach((entry) => {
                    this.metric(EVENTS.PERFORMANCE.LONG_TASK, entry.duration, {
                        name: entry.name,
                        context: {
                            startTime: entry.startTime,
                        },
                    });
                });
            });

            // Start observing long tasks
            observer.observe({ entryTypes: ['longtask'] });

            // Return cleanup function
            return () => observer.disconnect();
        }
        return () => {};
    }
}

// Export a singleton instance of the Tracking class
export const tracking = new Tracking();
